import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CenterLoader from 'components/CenterLoader';
import { useCreateCustomization } from 'hooks/useCreateCustomization';
import { useGetCustomization } from 'hooks/useGetCustomization';
import { useUpdateCustomization } from 'hooks/useUpdateCustomization';
import { Fragment, useState } from 'react';
import { ClipLoader } from 'react-spinners';
import { useSearchAiPluginStore } from 'store/searchAiPluginStore';
import { classNames } from 'util/Funcs';
import Customization from './Customization';
import DesktopAccessMessage from './DesktopAccessMessage';
import Leads from './Leads';
import PreviewTesting from './PreviewTesting';
import { Setup } from './Setup';

const tabs = [{ name: 'Set up' }, { name: 'Customization' }, { name: 'Preview & Testing' }, { name: 'Leads' }];

export default function Tabs() {
    const [currentTab, setCurrentTab] = useState('Set up');
    const { customization } = useSearchAiPluginStore();

    const { isLoading } = useGetCustomization();
    const { mutate, isPending } = useCreateCustomization();
    const { mutate: updateCustomization, isPending: isUpdating } = useUpdateCustomization();

    const renderWithDesktopMessage = (Component: React.ComponentType) => (
        <>
            <DesktopAccessMessage />
            <div className="hidden lg:block">
                <Component />
            </div>
        </>
    );

    const tabContentMap: Record<string, JSX.Element> = {
        'Set up': renderWithDesktopMessage(Setup),
        Customization: renderWithDesktopMessage(Customization),
        'Preview & Testing': renderWithDesktopMessage(PreviewTesting),
        Leads: <Leads />,
    };

    const renderContent = () => tabContentMap[currentTab];

    const handleClick = () => (customization.id ? updateCustomization() : mutate());

    if (isLoading) return <CenterLoader />;

    return (
        <Fragment>
            <div className="px-8 sm:px-6 lg:px-12 py-3 bg-white">
                <div className="sm:hidden">
                    <label htmlFor="tabs" className="sr-only">
                        Select a tab
                    </label>
                    <select
                        id="tabs"
                        name="tabs"
                        value={currentTab}
                        onChange={(e) => setCurrentTab(e.target.value)}
                        className="block w-full rounded-md border-gray-300 py-3 pl-3 pr-10 text-base focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
                    >
                        {tabs.map((tab) => (
                            <option key={tab.name}>{tab.name}</option>
                        ))}
                    </select>
                </div>
                <div className="hidden sm:block ">
                    <div className="flex md:justify-between sm:justify-center">
                        <div className="border-b border-gray-200">
                            <nav aria-label="Tabs" className="-mb-px flex space-x-8">
                                {tabs.map((tab) => (
                                    <button
                                        key={tab.name}
                                        onClick={() => setCurrentTab(tab.name)}
                                        aria-current={currentTab === tab.name ? 'page' : undefined}
                                        className={classNames(
                                            currentTab === tab.name
                                                ? 'border-indigo-500 text-indigo-600'
                                                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                                            'whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium',
                                            tab.name === 'Preview & Testing' ? 'sm:hidden md:block' : '',
                                        )}
                                    >
                                        {tab.name}
                                    </button>
                                ))}
                            </nav>
                        </div>
                        {isPending || isUpdating ? (
                            <ClipLoader loading={isPending || isUpdating} color="#039487" />
                        ) : (
                            <div className="self-center">
                                <button
                                    className="bg-[#039487] text-white px-10 h-12 hidden md:flex rounded-md  items-center gap-2 cursor-pointer"
                                    onClick={handleClick}
                                >
                                    <PlayArrowIcon />
                                    Save & Set Live
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className="mt-4 px-8 sm:px-6 lg:px-12">{renderContent()}</div>
        </Fragment>
    );
}
